import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Users, Globe2, Shield, Lightbulb, Target, Heart } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { OFFICES, STATS } from "@/constants/siteData";
import { SID } from "@/constants/siteIds";

const VALUES = [
  { Icon: Shield, t: "Trust by design", d: "Every record we touch is secured, audited and verifiable, because compliance is built into the product, not bolted on." },
  { Icon: Users, t: "Built with practitioners", d: "Real UK company secretaries, accountants and legal teams shape every release. We ship what helps, not what looks good in demos." },
  { Icon: Lightbulb, t: "AI where it matters", d: "We use AI to remove repetitive admin, minute taking, document drafting, deadline triage, never to replace human judgement." },
  { Icon: Target, t: "UK-native, by choice", d: "Companies Act 2006, ECCT Act 2023, Companies House gateway. We go deep on UK statute so you don't have to." },
  { Icon: Globe2, t: "Built for the long term", d: "Software-only filing by 2027, evolving PSC rules, ECCTA reforms, we're already on the compliant rails." },
  { Icon: Heart, t: "Service obsessed", d: "Real humans on support, 24/7. We pick up the phone." },
];

const TIMELINE = [
  { year: "2019", t: "CoSec Global founded", d: "Started in London with a single goal: make UK company secretarial work less painful." },
  { year: "2021", t: "Companies House gateway certified", d: "Became an Authorised Software Provider for direct electronic filing." },
  { year: "2023", t: "ISO 27001 certified", d: "Plus ISO 9001 and ISO 27701, independent assurance across quality, security and privacy." },
  { year: "2024", t: "AI minute taking shipped", d: "First UK Cosec platform with AI-assisted board minutes and document drafting." },
  { year: "2025", t: "CoVerify launched", d: "Identity verification module, ready for ECCT Act 2023." },
  { year: "2026", t: "Companies House 2027 ready", d: "Already aligned with the upcoming software-only filing mandate." },
];

export default function AboutPage() {
  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-12 max-w-4xl">
          <span className="pill">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> About CoSec Global
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            We make UK corporate compliance{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">disappear.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            CoSec Global is the AI-powered platform behind CoSec App and CoVerify, used by UK
            company secretarial teams, accountants and corporate law firms to manage entities,
            file with Companies House, run boards and verify identities, all from one secure
            cloud environment.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-cv py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="text-center md:text-left border-l-2 md:border-l-2 border-accent pl-4">
              <div className="font-display text-3xl md:text-4xl font-bold text-primary">{s.k}</div>
              <div className="text-xs md:text-sm text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-24">
        <div className="container-cv grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">Our mission</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              Compliance shouldn&apos;t feel like{" "}
              <span className="text-accent">paperwork.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p>
              UK company secretarial work is high-stakes, a missed confirmation statement can strike
              a company off the register, and high-volume, with most of it being deadline-driven
              admin. We saw teams burning through hours on copy-paste filings, version-controlled
              spreadsheets and inbox chases. That&apos;s the kind of work AI handles best.
            </p>
            <p>
              CoSec Global was built to take the repetitive parts off the desks of UK practitioners
              so they can focus on the judgement calls, the parts of governance that actually
              matter. We ship software that turns weeks of manual admin into hours.
            </p>
            <p>
              Today, CoSec App is the platform of choice for UK Cosec teams, accountants delivering
              Cosec as a service, and corporate law practices managing groups and PE portfolios.
              CoVerify, our identity verification module, is ready for the
              ECCT Act 2023 reforms.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
        <div className="container-cv">
          <div className="max-w-3xl">
            <span className="section-eyebrow">What we believe</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              The principles that shape what we build.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map(({ Icon, t, d }, i) => (
              <div key={i} data-testid={SID.aboutValue(i + 1)} className="rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all">
                <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold text-primary">{t}</div>
                <div className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-24">
        <div className="container-cv">
          <div className="max-w-3xl">
            <span className="section-eyebrow">Our journey</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              Built step by step{" "}
              <span className="text-accent">with UK teams.</span>
            </h2>
          </div>
          <ol className="mt-10 relative">
            <div className="absolute left-[1.05rem] top-0 bottom-0 w-px bg-border" />
            {TIMELINE.map((e, i) => (
              <li key={i} className="relative pl-12 pb-8 last:pb-0">
                <span className="absolute left-0 top-1 grid h-9 w-9 place-items-center rounded-full bg-accent text-primary font-display font-bold text-[10px]">
                  {e.year}
                </span>
                <div className="font-display text-lg font-bold text-primary">{e.t}</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-xl leading-relaxed">{e.d}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Offices */}
      <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
        <div className="container-cv">
          <div className="max-w-3xl">
            <span className="section-eyebrow">Where we are</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              Offices across the UK &amp; India.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {OFFICES.map((o, i) => (
              <div key={i} className="rounded-2xl border border-border bg-white p-6">
                <div className="font-display text-lg font-bold text-primary">{o.city}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{o.address}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Let&apos;s build the future of UK compliance{" "}
              <span className="text-accent">together.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Talk to us <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
